import { createContext, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { PushImplementation, NotificationPermission } from "../../lib/push/interface";
import { SplashScreen } from "../screens/splashScreen";
import { supabase } from "./supabase";
import { isDisposable } from "../../lib/util/isDisposable";

// `isExpoApp` is declared globally in `../../lib/nativeBridge.ts` (the single
// source of truth for the native bridge globals); only declare the one global
// this file adds.
declare global {
  interface Window {
    webkit: any;
  }
}

const expoPushCapability =
  window.isExpoApp &&
  window.ReactNativeWebView;

const iosPushCapability =
  window.webkit &&
  window.webkit.messageHandlers &&
  window.webkit.messageHandlers['push-permission-request'] &&
  window.webkit.messageHandlers['push-permission-state'];

export function PushNotificationsProvider({ children }: React.PropsWithChildren) {
  const [loading, setLoading] = useState(false);
  const [pushImplementation, setPushImplementation] = useState<PushImplementation>(new NoopPushImplementation());

  useEffect(() => {
    if (expoPushCapability) {
      console.log('has expoPushCapability');
      import('../../lib/push/expoPushImplementation')
        .then(({ ExpoPushImplementation }) => {
          const impl = new ExpoPushImplementation(supabase);
          impl.initialize();
          setPushImplementation(impl);
          setLoading(false);
          console.log('initialized expo push implementation');
        }).catch((e) => {
          console.error('Error initializing Expo push implementation', e);
          Sentry.captureException(e);
          setLoading(false);
        });
    } else if (iosPushCapability) {
      console.log('has iosPushCapability');
      import('../../lib/push/iosPushImplementation')
        .then(({ IosPushImplementation }) => {
          const impl = new IosPushImplementation(supabase);
          impl.initialize();
          setPushImplementation(impl);
          setLoading(false);
          console.log('initialized ios push implementation');
        }).catch((e) => {
          console.error('Error initializing iOS push implementation', e);
          Sentry.captureException(e);
          setLoading(false);
        });
    } else {
      console.log('no push capability');
      setLoading(false);
    }

    return () => {
      if (isDisposable(pushImplementation)) {
        pushImplementation[Symbol.dispose]();
      }
    }

  }, []);
  
  if (loading) {
    return <SplashScreen />
  }
  
  return <PushNotificationsContext.Provider value={pushImplementation}>
    {children}
  </PushNotificationsContext.Provider>
}

class NoopPushImplementation implements PushImplementation {
  async register() {
    return 'not-supported' as NotificationPermission;
  }
  
  getPermissionState() {
    return 'not-supported' as NotificationPermission;
  }
  
  setBadgeCount() {}
  
  clearBadgeCount() {}
}

// Exported so tests can render components against a specific permission state —
// the real implementations are chosen from platform globals at provider mount.
export const PushNotificationsContext = createContext<PushImplementation>(new NoopPushImplementation());

export function usePushNotifications() {
  return useContext(PushNotificationsContext);
}

